import { memo } from 'react';

import { useUploadsTrackingSession } from '@air/feature-uploads/hooks';
import { completedUploadCountSelector, failedUploadCountSelector } from '@air/redux-uploader';

import { useCancelAllPrivateUploads } from '~/components/Upload/hooks/useCancelAllPrivateUploads';
import { MainSideNavOverlayTray } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTray';
import { MainSideNavOverlayTrayHeader } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTrayHeader';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { getWorkspaceUploadsHeader } from '~/components/v2/components/workspace/workspace-uploads/getWorkspaceUploadsHeader';
import { useWorkspaceUploadsActions } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsActions';
import { useWorkspaceUploadsClearRecentUploads } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsClearRecentUploads';
import { useWorkspaceUploadsInProgressCounts } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsInProgressCounts';
import { useWorkspaceUploadsProgressingLabel } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsProgressingLabel';
import { WorkspaceUploadsEmptyState } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsEmptyState';
import { WorkspaceUploadsHeaderMenu } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsHeaderMenu';
import { WorkspaceUploadsPauseResumeAllButton } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsPauseResumeAllButton';
import { WorkspaceUploadsVirtualizedList } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsVirtualizedList';
import { WORKSPACE_UPLOADS_TRAY_ID } from '~/constants/workspaceActivity';
import { useAirSelector } from '~/utils/ReduxUtils';

export const WorkspaceUploadsTray = memo(() => {
  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'uploads' });
  const { onUploadFiles, onUploadFolders } = useWorkspaceUploadsActions();
  const { isOnline } = useUploadsTrackingSession();
  const { progressingLabel } = useWorkspaceUploadsProgressingLabel();
  const completedCount = useAirSelector(completedUploadCountSelector);
  const failedCount = useAirSelector(failedUploadCountSelector);
  const hasFailedUploads = failedCount > 0;
  const { hasInProgressUploads, inProgressCount, isPaused } = useWorkspaceUploadsInProgressCounts();
  const { cancelAllUploads } = useCancelAllPrivateUploads({
    analyticsContext: 'workspace_uploads',
    deleteAllErrorContext: 'Failed to cancel all workspace uploads',
  });
  const { clearRecentUploads, hasClearableUploads } = useWorkspaceUploadsClearRecentUploads();
  const hasCompletedUploads = completedCount > 0;
  const hasSessionUploads = hasInProgressUploads || hasFailedUploads || hasCompletedUploads;
  const { description: headerDescription, title: headerTitle } = getWorkspaceUploadsHeader({
    completedCount,
    failedCount,
    inProgressCount,
    isPaused,
    progressingLabel,
  });

  return (
    <MainSideNavOverlayTray
      contentId={WORKSPACE_UPLOADS_TRAY_ID}
      contentTestId="WORKSPACE_UPLOADS_PANEL"
      onOpenChange={onOpenChange}
      open={isOpen}
      overlayTestId="WORKSPACE_UPLOADS_OVERLAY"
      placement="bottom"
      shrinkIntoTrigger
    >
      <MainSideNavOverlayTrayHeader
        description={headerDescription}
        rightSuffix={
          <div className="flex shrink-0 items-center gap-1">
            {hasInProgressUploads ? <WorkspaceUploadsPauseResumeAllButton isOnline={isOnline} /> : null}
            <WorkspaceUploadsHeaderMenu
              onCancelAllUploads={hasInProgressUploads ? cancelAllUploads : undefined}
              onClearRecentUploads={hasClearableUploads ? clearRecentUploads : undefined}
              onUploadFiles={onUploadFiles}
              onUploadFolders={onUploadFolders}
            />
          </div>
        }
        title={headerTitle}
      />
      {hasSessionUploads ? (
        <WorkspaceUploadsVirtualizedList isOnline={isOnline} />
      ) : (
        <WorkspaceUploadsEmptyState onUploadFiles={onUploadFiles} />
      )}
    </MainSideNavOverlayTray>
  );
});

WorkspaceUploadsTray.displayName = 'WorkspaceUploadsTray';
